import {
	SOCIAL_MEDIA_PLATFORMS,
	SOCIAL_MEDIA_PLATFORMS_MAPPED,
	type SocialMediaPlatform,
} from './socialMedia';
import { type SingleBulletinApplicant } from './bulletins';
import {
	type SingleExchangeConversation,
} from './conversations';
import { type BaseTransaction } from './transactions';
import { type Agreement } from './agreements';
import { commaSeparate } from '@/utils/helpers';
import { academicYearsObj } from '@/utils/academicYears';
import { interestsObj } from '@/utils/interests';
import { majorsObj } from '@/utils/majors';
import { sportsObj } from '@/utils/sports';
import { transactionTypesObj } from '@/utils/transactions';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import {
	type AcademicYear,
	type Interest,
	type Sport,
	SOCIAL_MEDIA_PLATFORM_IDS,
	type TransactionType,
	type ApiV1,
	type OnboardingStatus,
} from '@teamworksdev/influencer/core';
import { handleError } from '@/utils/errorHandling';


type BaseExchangeAthleteProvider = SingleExchangeAthlete | SingleBulletinApplicant | SingleExchangeConversation | Agreement | BaseTransaction;

const isBaseTransactionAthleteProvider = (data: BaseExchangeAthleteProvider): data is BaseTransaction => {
	return 'athlete' in data;
};
const isAgreementAthleteProvider = (data: BaseExchangeAthleteProvider): data is Agreement => {
	return 'payeeUuid' in data;
};
const isSingleExchangeConversationAthleteProvider = (data: BaseExchangeAthleteProvider): data is SingleExchangeConversation => {
	return 'athleteUuid' in data;
};
const iSingleBulletinApplicantAthleteProvider = (data: BaseExchangeAthleteProvider): data is SingleBulletinApplicant => {
	return 'applicantUuid' in data;
};
const isSingleExchangeAthleteProvider = (data: BaseExchangeAthleteProvider): data is SingleExchangeAthlete => {
	return 'uuid' in data && 'fullName' in data && 'profileImage' in data;
};
class BaseExchangeAthlete {
	uuid: string;
	fullName: string;
	profileImage: string | null;
	constructor(data: BaseExchangeAthleteProvider) {
		switch (true) {
			case isBaseTransactionAthleteProvider(data):
				this.uuid = data.athlete?.uuid || '';
				this.fullName = data.athlete?.fullName || '';
				this.profileImage = null;
				break;
			case isAgreementAthleteProvider(data):
				this.uuid = data.payeeUuid;
				this.fullName = data.payeeName;
				this.profileImage = null;
				break;
			case isSingleExchangeConversationAthleteProvider(data):
				this.uuid = data.athleteUuid;
				this.fullName = data.fullName;
				this.profileImage = data.profileImage;
				break;
			case iSingleBulletinApplicantAthleteProvider(data):
				this.uuid = data.applicantUuid || '';
				this.fullName = data.fullName || '';
				this.profileImage = data.profileImage;
				break;
			case isSingleExchangeAthleteProvider(data):
				this.uuid = data.uuid;
				this.fullName = data.fullName;
				this.profileImage = data.profileImage;
				break;
			default:
				throw new Error('Incompatible Athlete Data');
		}
	}
}

type SingleExchangeAthlete = {
	uuid: string;
	firstName: string;
	lastName: string;
	fullName: string;
	profileImage: string | null;
	sport: string;
	schoolName: string;
	teamName: string;
	rateCardsCount?: number;
	influentialUrl?: string | null;
	socialStats?: ApiV1.SingleExchangeAthleteSocialStats | null;
	hometown?: string | null;
	hometownState?: string | null;
	phone?: string | null;
	email?: string | null;
	major?: string | null;
	academicYear?: string | null;
	jerseyNumber?: string | null;
	position?: string | null;
	schoolLogo?: string | null;
	schoolLocation?: string | null;
	conference?: string | null;
	conferenceSize?: string | null;
	bio?: string | null;
	whyWorkWithMe?: string | null;
	interests?: string[];
	agents?: ApiV1.ExchangeAthletePublicAgent[];
	agentName?: string | null;
	agentEmail?: string | null;
	agentPhone?: string | null;
	lastSeen?: string | null;
	group?: string | null;
	timeZone?: string | null;
	profileName?: string | null;
	socialProfiles?: SingleExchangeAthleteLink[];
	associatedLists?: Pick<ApiV1.UserList, 'name' | 'uuid'>[];
	rateCards?: SingleExchangeAthleteRateCard[];
	links?: SingleExchangeAthleteLink[];
	localExchange?: ApiV1.ExchangeAthleteLocalExchange;
	onboardingStatus?: OnboardingStatus;
	twrn?: string | null;
};

interface SingleExchangeAthleteLink {
	url: string;
	text: string;
	platform?: typeof SOCIAL_MEDIA_PLATFORMS[SocialMediaPlatform];
}

const isExchangeAthleteProfileExpanded = (data: ApiV1.ExchangeAthleteProfile | ApiV1.ExchangeAthleteProfileExpanded | ApiV1.ExchangeAthleteProfilePublic): data is ApiV1.ExchangeAthleteProfileExpanded => {
	return 'rate_cards' in data;
};

const isExchangeAthleteProfilePublic = (data: ApiV1.ExchangeAthleteProfile | ApiV1.ExchangeAthleteProfilePublic | ApiV1.ExchangeAthleteProfileExpanded): data is ApiV1.ExchangeAthleteProfilePublic => {
	return 'local_exchange' in data;
};

class ExchangeAthleteFilters {
	schools?: string[];
	sports?: Sport[];
	interests?: Interest[];
	conferences?: string[];
	social_providers?: ApiV1.ExchangeAthleteSocialProviderFilters;
	academic_years?: AcademicYear[];
	show_social?: boolean;
	list?: string[];

	constructor(params: any) {
		this.schools = params.schools;
		this.sports = params.sports;
		this.interests = params.interests;
		this.conferences = params.conferences;
		this.social_providers = params.social_providers;
		this.academic_years = params.academic_years;
		this.show_social = params.show_social;
		this.list = params.list;
	}
}

class ListAthletesExchangeProfile implements SingleExchangeAthlete {
	uuid;
	firstName;
	lastName;
	fullName;
	profileImage;
	sport;
	schoolName;
	teamName;
	rateCardsCount;
	influentialUrl;
	socialStats;

	constructor(data: ApiV1.ExchangeAthlete) {
		this.uuid = data.uuid;
		this.firstName = data.first_name;
		this.lastName = data.last_name;
		this.fullName = `${data.first_name} ${data.last_name}`;
		this.profileImage = data.profile_image;
		this.sport = sportsObj[data.sport_type];
		this.schoolName = data.school;
		this.teamName = data.team;
		this.rateCardsCount = data.rate_cards;
		this.influentialUrl = data.influential_url;
		this.socialStats = data.social_stats ?? null;
	}
}

class SingleExchangeAthleteRateCard {
	uuid: string;
	title: string | null;
	transactionType: string;
	transactionTypeId: TransactionType;
	cost: number;
	description: string;

	constructor(data: ApiV1.ExchangeAthleteRateCard) {
		this.uuid = data.uuid;
		this.title = data.title;
		this.transactionType = transactionTypesObj[data.transaction_type_id];
		this.transactionTypeId = data.transaction_type_id;
		this.cost = data.cost;
		this.description = data.description;
	}
}

class SingleAthleteExchangeProfile implements SingleExchangeAthlete {
	uuid: string;
	firstName: string;
	lastName: string;
	fullName: string;
	profileImage: string | null;
	sport: string;
	schoolName: string;
	teamName: string;
	rateCardsCount: number;
	influentialUrl: string | null;
	hometown: string | null;
	hometownState: string | null = null;
	phone: string | null = null;
	email: string | null;
	major: string | null;
	academicYear: string | null = null;
	jerseyNumber: string | null = null;
	position: string | null = null;
	schoolLogo: string | null;
	schoolLocation: string | null;
	conference: string | null;
	conferenceSize: string | null;
	bio: string | null;
	whyWorkWithMe: string | null;
	interests: string[];
	agents: ApiV1.ExchangeAthletePublicAgent[] = [];
	associatedLists: SingleExchangeAthlete['associatedLists'];
	rateCards: SingleExchangeAthlete['rateCards'];
	agentName: string | null;
	agentEmail: string | null;
	agentPhone: string | null = null;
	lastSeen: string | null;
	group: string | null;
	timeZone: string | null;
	profileName: string | null;
	socialProfiles: SingleExchangeAthlete['socialProfiles'];
	links: SingleExchangeAthlete['links'];
	localExchange: ApiV1.ExchangeAthleteLocalExchange;
	onboardingStatus: OnboardingStatus;
	twrn: string | null;

	constructor(data: ApiV1.ExchangeAthleteProfile | ApiV1.ExchangeAthleteProfileExpanded | ApiV1.ExchangeAthleteProfilePublic) {
		this.uuid = data.uuid;
		this.firstName = data.first_name;
		this.lastName = data.last_name;
		this.fullName = `${data.first_name} ${data.last_name}`;
		this.profileImage = data.profile_image;
		this.schoolName = data.school.name;
		this.schoolLogo = data.school.logo;
		this.teamName = data.team.name;
		this.bio = data.athlete_exchange_profile.bio;

		this.socialProfiles = data.social_profiles
			.filter((i) => {
				return i.public_profile_url
					&& i.social_provider !== SOCIAL_MEDIA_PLATFORM_IDS.LINKEDIN
					&& i.social_provider !== SOCIAL_MEDIA_PLATFORM_IDS.FACEBOOK;
			})
			.map((i) => {
				const platform = SOCIAL_MEDIA_PLATFORMS_MAPPED[i.social_provider];
				return {
					url: i.public_profile_url!,
					text: i.username ?? platform?.name ?? '',
					platform: SOCIAL_MEDIA_PLATFORMS_MAPPED[i.social_provider]!,
				};
			});

		this.links = [];

		[
			SOCIAL_MEDIA_PLATFORM_IDS.LINKEDIN,
			SOCIAL_MEDIA_PLATFORM_IDS.FACEBOOK,
		].forEach((id) => {
			const platform = SOCIAL_MEDIA_PLATFORMS_MAPPED[id];
			const socialProfile = data.social_profiles.find((i) => i.social_provider === id);
			socialProfile && socialProfile.public_profile_url && this.links?.push({
				url: socialProfile.public_profile_url,
				text: socialProfile.username ?? platform?.name ?? '',
				platform: platform!,
			});
		});

		data.athlete_exchange_profile.links?.forEach((link) => {
			this.links?.push({
				url: link,
				text: link,
			});
		});

		if (isExchangeAthleteProfilePublic(data)) {
			this.localExchange = data.local_exchange;
			this.rateCards = data.athlete_exchange_profile.rates.map((rateCard) => new SingleExchangeAthleteRateCard(rateCard));
			this.rateCardsCount = this.rateCards.length;
			this.profileName = data.athlete_exchange_profile.profile_name;
			this.whyWorkWithMe = data.athlete_exchange_profile.why_work_with_me;
			this.sport = data.sport_name;
			this.academicYear = data.academic_year != null
				? academicYearsObj[data.academic_year]
				: null;
			this.jerseyNumber = data.jersey_number;
			this.position = data.position;
			this.hometownState = data.hometown_state;
			this.major = data.major != null
				? majorsObj[data.major]
				: null;
			this.interests = data.interests.map((i) => interestsObj[i]);
			this.agents = data.agents;
		} else {
			this.schoolLocation = commaSeparate(
				data.school.city,
				data.school.state,
			);
			this.hometown = commaSeparate(
				data.athlete_exchange_profile.home_city,
				data.athlete_exchange_profile.home_state,
			);
			this.email = data.email;
			this.major = data.athlete_exchange_profile.major_id
				? majorsObj[data.athlete_exchange_profile.major_id]
				: null;
			this.sport = sportsObj[data.team.sport_type];
			this.conference = data.school.conference?.name ?? null;
			this.conferenceSize = data.school.segment?.name ?? null;

			this.whyWorkWithMe = data.athlete_exchange_profile.why_work_with_me;
			this.interests = data.athlete_exchange_profile.interests?.map(
				(i) => interestsObj[i],
			) ?? [];
			this.agentName = data.athlete_exchange_profile.representation_contact_name;
			this.agentEmail = data.athlete_exchange_profile.representation_email;
			this.lastSeen = data.last_seen;
			this.group = data.group;
			this.timeZone = data.team.timezone;
			data.influential_url && this.links.push({
				url: data.influential_url,
				text: 'Influential',
				platform: SOCIAL_MEDIA_PLATFORMS['influential'],
			});
			this.onboardingStatus = data.onboarding_status;
			this.twrn = (data as any).twrn ?? null;

			const repPhone = data.athlete_exchange_profile.representation_phone;
			if (repPhone) {
				try {
					this.agentPhone = parsePhoneNumberWithError(repPhone, 'US').formatInternational();
				} catch (error) {
					handleError(error, 'none');
				}
			}

			if (data.phone) {
				try {
					this.phone = parsePhoneNumberWithError(data.phone, 'US').formatInternational();
				} catch (error) {
					handleError(error, 'none');
				}
			}
		}

		if (isExchangeAthleteProfileExpanded(data)) {
			this.associatedLists = data.lists;
			this.rateCards = data.rate_cards.map((rateCard) => new SingleExchangeAthleteRateCard(rateCard));
		}
	}
}

export {
	BaseExchangeAthlete,
	type BaseExchangeAthleteProvider,
	type SingleExchangeAthlete,
	type SingleExchangeAthleteLink,
	ExchangeAthleteFilters,
	ListAthletesExchangeProfile,
	SingleExchangeAthleteRateCard,
	SingleAthleteExchangeProfile,
};
